import { useMemo, useState } from 'react'
import { Alert, Button, Popconfirm, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BarChartOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatBytes, formatDate, formatDuration } from '@/shared/lib/format'
import { Can } from '@/shared/auth/Can'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ClosedHiddenTag } from '@/shared/ui/table/ClosedHidden'
import { useClosedHidden } from '@/shared/ui/table/useClosedHidden'
import { STATE_ALL, STATE_OPEN_ONLY } from '@/shared/ui/table/closedFilter'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { CampaignFormModal } from '@/features/venue/CampaignFormModal'
import { CampaignStatsModal } from '@/features/venue/CampaignStatsModal'
import { fetchVenues } from '@/features/venue/venueApi'
import {
  CAMPAIGN_STATES,
  CAMPAIGN_STATE_FILTER,
  countClosedCampaigns,
  deleteCampaign,
  fetchCampaignPage,
  setCampaignState,
  type AdCampaign,
  type CampaignState,
} from '@/features/venue/campaignApi'

const STATE_COLOR: Record<CampaignState, string> = {
  DRAFT: 'default',
  ACTIVE: 'green',
  PAUSED: 'gold',
  FINISHED: 'default',
}

/**
 * Reklama kampaniyalari — admin ro'yxati.
 *
 * ⚠️ PUL BU YERDA YO'Q: faqat ko'rsatish shartlari (mukofot, davr, kunlik
 * chegara). Hisob-kitob `ledger` da (`CampaignController.java` javadoc).
 */
export function VenueCampaignsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<AdCampaign | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [statsTarget, setStatsTarget] = useState<AdCampaign | null>(null)
  const [error, setError] = useState<string | null>(null)

  /*
    ⚠️ OB'EKT TANLOVI ENDI HOLAT MANBAI — MANZIL.

    Ilgari u `useState(searchParams.get('venueId'))` edi: havoladagi
    qiymat BIR MARTA o'qilar, keyin holat ajralib ketardi. Ya'ni
    tanlovni o'zgartirgach manzil eskisicha qolar, sahifa
    yangilanganda esa tanlov o'sha eski qiymatga QAYTARDI.

    ⚠️ Parametr nomi `venueId` bo'lib QOLADI (`f.venue` emas): ob'ekt
    kartochkasidan bu sahifaga aynan shu nom bilan havola qilinadi va
    uni o'zgartirish mavjud havolalarni jimgina buzardi.
  */
  const [searchParams, setSearchParams] = useSearchParams()
  const venueId = searchParams.get('venueId')
  const setVenueId = (next: string | null) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        if (next === null) params.delete('venueId')
        else params.set('venueId', next)
        return params
      },
      { replace: true },
    )
  }

  /*
    ⚠️ SAHIFALASH, QIDIRUV, SARALASH VA HOLAT FILTRI — SERVERDA.
    `GET /api/venues/campaigns` `page`/`size`/`search`/`sortField`/
    `sortOrder` (2026-08-20) va `state`/`includeClosed` (2026-08-21) ni
    oladi.

    ⚠️ ESKI IZOH BEKOR QILINDI: bu yerda «holat filtri OLIB TASHLANDI,
    serverdagi yagona saqlangan filtr — venueId» deb yozilgan edi.
    Xulosa o'sha paytda to'g'ri edi (mijozdagi filtr sahifalangan
    ro'yxatda yolg'on javob berardi), lekin sabab yo'qoldi.

    ⚠️ TUGAGAN KAMPANIYALAR SUKUT BO'YICHA KO'RINMAYDI (ADR-033).
    «Yopiq» — FAQAT `FINISHED`; `DRAFT` va `PAUSED` QOLADI, ular
    tugagan emas.
  */
  const venues = useQuery({ queryKey: queryKeys.venues.all, queryFn: () => fetchVenues(null) })

  // ⚠️ `useMemo` SHART: har chizishda yangi funksiya `DataTable` ning
  //    so'rovini qayta ishga tushirardi (`queryFn` havolasi o'zgaradi).
  const fetchPage = useMemo(() => fetchCampaignPage(venueId), [venueId])
  const countClosed = useMemo(() => countClosedCampaigns(venueId), [venueId])

  /*
    ⚠️ Holat tanlagichi jadval holatining O'ZINI yozadi — `DataTable`
    o'sha manbadan o'qiydi. Alohida `useState` ikkinchi haqiqat manbai
    bo'lardi va tanlov bilan so'rov ajralib ketardi.
  */
  const { state: tableState, patch: patchTable } = useTableUrlState()
  const stateFilter = tableState.filters[CAMPAIGN_STATE_FILTER]?.[0] ?? STATE_OPEN_ONLY
  const setStateFilter = (value: string) =>
    patchTable({ page: 1, filters: { ...tableState.filters, [CAMPAIGN_STATE_FILTER]: [value] } })

  const hiddenCounts = useClosedHidden<AdCampaign>({
    queryKey: queryKeys.campaigns.all(venueId),
    state: tableState,
    fetchPage,
    countClosed,
    enabled: stateFilter === STATE_OPEN_ONLY,
  })

  /**
   * Holat ro'yxati — tanlagich va USTUN filtri BITTA manbadan.
   *
   * ⚠️ Ustunda `filters` MAJBURIY: `DataTable.handleChange` filtrlarni
   * antd bergan ro'yxatdan qayta quradi, antd esa faqat `filters`
   * e'lon qilgan ustunlarni biladi. Usiz tanlov sahifa
   * almashtirilishida jimgina sukutga qaytardi.
   */
  const stateOptions = [
    { value: STATE_OPEN_ONLY, label: t('dataTable.stateOpenOnly') },
    ...CAMPAIGN_STATES.map((s) => ({ value: s, label: t(`campaignState.${s}`) })),
    { value: STATE_ALL, label: t('dataTable.stateAll') },
  ]

  const changeState = useMutation({
    mutationFn: (values: { id: string; state: CampaignState }) => setCampaignState(values.id, values.state),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(venueId) }),
    onError: (err) => setError(translateError(err, t)),
  })

  /*
    ⚠️⚠️ O'CHIRISH QATTIQ — bazadan `DELETE`, holat bayrog'i emas
    (`deleteCampaign` sarlavhasi). Shuning uchun TASDIQ shart va u
    "ishonchingiz komilmi?" demaydi: nima bo'lishini AYTADI.

    ⚠️ `Popconfirm`, `Modal.confirm` EMAS. Sabab o'lchovda: shart
    tufayli (`DRAFT` + 0 ko'rsatuv) bu amalda YO'QOLADIGAN TARIX YO'Q,
    ya'ni to'liq ekranli tasdiq oynasi og'irlikda haqiqiy xavfga mos
    kelmasdi. Qaytarib bo'lmaydigan, LEKIN tarixni yo'qotadigan
    amallarda naqsh boshqacha (`NasDeleteModal`: kodni qo'lda terish).

    ⚠️ Tasdiq QATOR MENYUSIGA bog'langan (`pendingDelete`), menyu
    bandining ICHIGA emas: menyu bosilganda YOPILADI va uning ichida
    ochilgan qalqib chiquvchi oyna o'sha zahoti yo'qolardi.
  */
  const [pendingDelete, setPendingDelete] = useState<AdCampaign | null>(null)

  const remove = useMutation({
    mutationFn: (id: string) => deleteCampaign(id),
    onSuccess: () => {
      setPendingDelete(null)
      setError(null)
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(venueId) })
    },
    /*
      ⚠️ 409 EKRANDA QOLADI. Panel `DRAFT` ni ko'radi, lekin
      ko'rsatuvlar sonini KO'RMAYDI — ya'ni «bu qoralamada allaqachon
      ko'rsatuvlar bor» degan rad javobi faqat serverdan keladi va uni
      yutib yuborish «bosdim, hech narsa bo'lmadi» bo'lib o'qilardi.
    */
    onError: (err) => {
      setPendingDelete(null)
      setError(translateError(err, t))
    },
  })

  const venueName = (id: string | null) =>
    id === null ? t('campaign.allVenues') : (venues.data ?? []).find((v) => v.id === id)?.name ?? id

  const columns: ColumnsType<AdCampaign> = [
    {
      key: 'name',
      title: t('campaign.name'),
      sorter: true,
      /*
        ⚠️ ENI BERILDI (tester-2, BUG-015). Qolgan olti ustunda qat'iy en
        bor, bu ustunda esa yo'q edi — ya'ni u «qolganini oladi». O'lchov
        (1440px, uz): unga atigi 166px tegardi va ichida IKKI qator matn
        turadi (kampaniya nomi + reklama beruvchi), ya'ni eng ko'p joy
        kerak bo'lgan ustun eng torlaridan biri edi.
      */
      width: 240,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.advertiserName}
          </Typography.Text>
        </Space>
      ),
    },
    { key: 'venue', title: t('campaign.venue'), width: 170, render: (_, row) => venueName(row.venueId) },
    {
      key: 'reward',
      title: t('campaign.reward'),
      width: 160,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {row.rewardBytes !== null ? <Typography.Text>{formatBytes(row.rewardBytes)}</Typography.Text> : null}
          {row.rewardSeconds !== null ? <Typography.Text>{formatDuration(row.rewardSeconds)}</Typography.Text> : null}
          {row.rewardBytes === null && row.rewardSeconds === null ? (
            <Typography.Text type="secondary">{t('campaign.rewardFromZone')}</Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      /*
        ⚠️ Saralash BOSHLANISH sanasi bo'yicha (`STARTS_AT`): ustunda
        ikki sana turadi, server esa bittasi bo'yicha tartiblaydi.
        Tugash sanasi bo'yicha enum ham bor, lekin ikkita saralash
        strelkasi bitta ustunda ko'rsatib bo'lmaydi — «qaysi kampaniya
        keyingi ishga tushadi» amaliyotda so'raladigan savol.
      */
      key: 'period',
      title: t('campaign.period'),
      width: 200,
      sorter: true,
      render: (_, row) => `${formatDate(row.startsAt)} — ${formatDate(row.endsAt)}`,
    },
    {
      key: 'cap',
      /*
        ⚠️ SARLAVHA QISQARTIRILDI, MA'NOSI TOOLTIPDA (tester-2, BUG-015).

        O'lchangan: yorliq «Kuniga bitta mehmonga chegara» — 29 belgi —
        110px katakda turardi va uch qatorga sinardi. Aynan shu ustun
        qo'shnilariga «yopishib» ko'rinardi, chunki sinib ketgan sarlavha
        katak chegarasini butunlay to'ldirib yuborardi.

        ⚠️ To'liq yorliq YO'QOLMADI: u formada (`CampaignFormModal`)
        o'sha-o'sha qoladi va bu yerda tooltipda. Jadval sarlavhasi va
        forma yorlig'i BOSHQA-BOSHQA vazifa: birinchisi ustunni ataydi,
        ikkinchisi maydonni tushuntiradi.
      */
      title: (
        <Tooltip title={t('campaign.dailyCapPerGuest')}>
          <span>{t('campaign.dailyCapShort')}</span>
        </Tooltip>
      ),
      width: 130,
      align: 'right',
      render: (_, row) => row.dailyCapPerGuest,
    },
    {
      // ⚠️ Ustun kaliti FILTR kaliti bilan bir xil — sabab
      // `stateOptions` izohida.
      key: CAMPAIGN_STATE_FILTER,
      title: t('subscribers.columns.status'),
      width: 110,
      sorter: true,
      filters: stateOptions.map((o) => ({ text: o.label, value: o.value })),
      filterMultiple: false,
      render: (_, row) => <Tag color={STATE_COLOR[row.state]}>{t(`campaignState.${row.state}`)}</Tag>,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        // Statistika huquqsiz (ko'rish), qolgani `campaign.manage` ostida;
        // tugatilgan kampaniyada holat amali umuman ko'rinmaydi.
        <Popconfirm
          open={pendingDelete?.id === row.id}
          title={t('campaign.deleteTitle')}
          description={
            <Space direction="vertical" size={2} style={{ maxWidth: 320 }}>
              <Typography.Text>{t('campaign.deleteSubject', { name: row.name })}</Typography.Text>
              {/* Oqibat OCHIQ aytiladi: yozuv butunlay ketadi va uni
                  qaytarib bo'lmaydi. Shart esa nima uchun bu xavfsiz
                  ekanini tushuntiradi — hali yurmagan qoralamada
                  yo'qoladigan tarix yo'q. */}
              <Typography.Text type="secondary">{t('campaign.deleteWarn')}</Typography.Text>
            </Space>
          }
          okText={t('campaign.delete')}
          okButtonProps={{ danger: true, loading: remove.isPending }}
          cancelText={t('common.cancel')}
          onConfirm={() => remove.mutate(row.id)}
          onCancel={() => setPendingDelete(null)}
          onOpenChange={(next) => {
            /*
              ⚠️ `open` BOSHQARILADI, ya'ni antd o'zi yopa olmaydi —
              faqat shu chaqiruvni beradi. Bu ataylab: so'rov ketayotgan
              paytda yopilish RAD ETILADI, aks holda tasdiq bosilgan
              zahoti oyna yo'qolib, «bosdim, hech narsa bo'lmadi» degan
              lahza qolardi. Tugma `loading` bilan turadi va natijani
              `onSuccess`/`onError` yopadi.
            */
            if (!next && !remove.isPending) setPendingDelete(null)
          }}
        >
          {/*
            ⚠️ `<span>` SHART, bezak emas: `Popconfirm` bolasini
            klonlab unga `ref` va hodisa ulaydi, `RowActions` esa
            oddiy funksional komponent — `ref` ni uzatmaydi. To'g'ridan
            to'g'ri o'ralsa React ogohlantirish berardi va qalqib
            chiquvchi oyna joyini topa olmasdi.
          */}
          <span>
            <RowActions
              items={[
                {
                  key: 'stats',
                  label: t('campaign.statsButton'),
                  icon: <BarChartOutlined />,
                  onClick: () => setStatsTarget(row),
                },
                {
                  key: 'edit',
                  label: t('tariffs.edit'),
                  onClick: () => {
                    setEditing(row)
                    setFormOpen(true)
                  },
                  permission: 'campaign.manage',
                },
                {
                  key: 'pause',
                  label: t('campaign.pause'),
                  onClick: () => changeState.mutate({ id: row.id, state: 'PAUSED' }),
                  permission: 'campaign.manage',
                  loading: changeState.isPending,
                  hidden: row.state === 'FINISHED' || row.state !== 'ACTIVE',
                },
                {
                  key: 'activate',
                  label: t('campaign.activate'),
                  onClick: () => changeState.mutate({ id: row.id, state: 'ACTIVE' }),
                  permission: 'campaign.manage',
                  loading: changeState.isPending,
                  hidden: row.state === 'FINISHED' || row.state === 'ACTIVE',
                },
                {
                  /*
                    ⚠️ FAQAT `DRAFT` DA CHIZILADI. Server boshqa holatda 409
                    qaytaradi, ya'ni band bosib bo'lmaydigan tugma bo'lardi —
                    «urinib ko'rdim, ishlamadi» degan foydasiz tajriba
                    (`AgentGovernance` dagi bilan bir xil qoida).

                    ⚠️ Bu shart YETARLI EMAS va shunday bo'lib qoladi:
                    `DRAFT` da ham ko'rsatuvlar bo'lishi mumkin (holatni
                    `ACTIVE` dan qaytarish taqiqlanmagan). O'sha holatni
                    faqat server biladi va rad javobi bannerda ko'rinadi.
                  */
                  key: 'delete',
                  label: t('campaign.delete'),
                  onClick: () => setPendingDelete(row),
                  permission: 'campaign.manage',
                  danger: true,
                  divider: true,
                  loading: remove.isPending && pendingDelete?.id === row.id,
                  hidden: row.state !== 'DRAFT',
                },
              ]}
            />
          </span>
        </Popconfirm>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.venueCampaigns')}
        description={t('campaign.intro')}
        extra={
          <Can permission="campaign.manage">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('campaign.createTitle')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/*
        ⚠️ Ustunlar eni yig'indisi: 56+240+170+160+200+130+110+56 = 1122.
        Kichik `scrollX` qoldirilsa antd ustunlarni SIQARDI va qo'shni
        kataklarning matni bir-biriga yopishib ko'rinardi (tester-2,
        BUG-015).

        ⚠️ Ro'yxat XATOSI, bo'sh holat va «filtrni tozalash» —
        hammasini `DataTable` o'zi chizadi.
      */}
      <DataTable<AdCampaign>
        queryKey={queryKeys.campaigns.all(venueId)}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="venue-campaigns"
        stickyFirstColumn
        bordered
        searchable
        searchPlaceholder={t('campaign.searchPlaceholder')}
        scrollX={1122}
        emptyTitle={t('campaign.emptyTitle')}
        /*
          ⚠️ Bo'sh ro'yxat «kampaniya yo'q» degani EMAS: tugaganlar
          sukut bo'yicha yashirin.
        */
        {...(hiddenCounts && hiddenCounts.hidden > 0
          ? {
              emptyDescription: t('dataTable.closedHiddenHint'),
              emptyAction: { label: t('dataTable.stateAll'), onClick: () => setStateFilter(STATE_ALL) },
            }
          : { emptyDescription: t('campaign.emptyHint') })}
        toolbarExtra={
          /*
            ⚠️ Ob'ekt tanlovi jadval holatida EMAS, manzildagi `venueId`
            da qoladi: ob'ekt kartochkasidan bu sahifaga aynan shu nom
            bilan havola qilinadi (yuqoridagi izoh). Shuning uchun u
            `DataTable` ning «filtrni tozalash» tugmasiga ham tushmaydi
            va alohida tozalanadi.
          */
          <Space wrap>
            {/*
              ⚠️ `allowClear` YO'Q: bo'sh maydon «filtr qo'yilmagan» deb
              o'qilardi, ro'yxat esa aslida tugaganlarsiz turgan bo'lardi.
            */}
            <Tooltip title={t('dataTable.closedHiddenHint')}>
              <Select<string>
                style={{ width: 190 }}
                aria-label={t('subscribers.columns.status')}
                value={stateFilter}
                onChange={setStateFilter}
                options={stateOptions}
              />
            </Tooltip>
            <ClosedHiddenTag counts={hiddenCounts} onShowAll={() => setStateFilter(STATE_ALL)} />
            {/*
              ⚠️ Ob'ektlar ro'yxati yiqilsa tanlov BO'SH bo'ladi va bu
              «tashkilotda ob'ekt yo'q» degan ma'noni berardi — operator
              filtrni ishlatishdan voz kechardi. Sabab va qayta urinish
              tanlovning O'RNIDA turadi.
            */}
            {venues.isError ? (
              <Space size={4}>
                <Typography.Text type="danger">{t('campaign.venuesLoadFailed')}</Typography.Text>
                <Button size="small" onClick={() => void venues.refetch()}>
                  {t('dataTable.retry')}
                </Button>
              </Space>
            ) : (
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: 260 }}
                placeholder={t('campaign.allVenues')}
                value={venueId ?? undefined}
                loading={venues.isPending}
                options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
                onChange={(value: string | undefined) => setVenueId(value ?? null)}
              />
            )}
            {venueId !== null ? (
              <Button size="small" type="link" onClick={() => setVenueId(null)}>
                {t('dataTable.clearFilters')}
              </Button>
            ) : null}
          </Space>
        }
      />

      <CampaignFormModal open={formOpen} campaign={editing} onClose={() => setFormOpen(false)} />
      <CampaignStatsModal campaign={statsTarget} onClose={() => setStatsTarget(null)} />
    </>
  )
}
